import { useCallback, useMemo, useRef, useState } from 'react'

import {
    packagingLotOption,
    scentingLotOption,
    sortingLotOption,
} from './lot-option-builders'
import type { LotOption } from './lot-items-field'
import type { ListLotsParams as SortingListParams } from '@/lib/api/sorting-lot-api'
import type { ListLotsParams as ScentingListParams } from '@/lib/api/scenting-lot-api'
import type { ListLotsParams as ProductListParams } from '@/lib/api/product-lot-api'
import type { ListLotsParams as PackagingListParams } from '@/lib/api/packaging-lot-api'
import { sortingLotApi } from '@/lib/api/sorting-lot-api'
import { scentingLotApi } from '@/lib/api/scenting-lot-api'
import { productLotApi } from '@/lib/api/product-lot-api'
import { productLotToOption } from '@/components/product-lots/product-lot-option'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useSortingLotsByIds,
    useSortingLotsInfinite,
} from '@/hooks/use-sorting-lot-query'
import {
    useScentingLotsByIds,
    useScentingLotsInfinite,
} from '@/hooks/use-scenting-lot-query'
import {
    useProductLotsByIds,
    useProductLotsInfiniteQuery,
} from '@/hooks/use-product-lot-query'
import {
    usePackagingLotsByIds,
    usePackagingLotsInfinite,
} from '@/hooks/use-packaging-lot-query'

// Nguồn dữ liệu DÙNG CHUNG cho mọi ô chọn lô (rule "Resource-select inputs" ở
// AGENTS.md): tìm phía server có debounce + cuộn tới đáy tải trang tiếp. Mỗi
// loại lô một hook, mọi chỗ chọn loại lô đó (sổ xưởng, phiếu nhập/xuất/lấy
// hàng) đều lấy từ đây thay vì tự `useXLots({ per_page: N })`.

/** Cỡ trang mỗi lần cuộn. Nhỏ để mở picker nhanh; cuộn là có thêm. */
export const LOT_PICKER_PAGE_SIZE = 30

export interface LotPickerSource {
    /** Seed (lô của bản ghi đang sửa) + mọi trang đã tải theo từ khoá hiện tại. */
    options: LotOption[]
    /** Tra option theo id kể cả khi nó đã rơi khỏi trang hiện tại (đổi từ khoá).
     *  Dùng cho nhãn trên nút, gợi ý "Còn X", kiểm vượt tồn. */
    getOption: (id: string | undefined) => LotOption | undefined
    onSearchChange: (search: string) => void
    /** Không có → picker không cuộn tải thêm (vd options cố định của một phiếu
     *  lấy hàng). Nguồn `useXLotSource` luôn có. */
    onLoadMore?: () => void
    hasMore?: boolean
    loadingMore?: boolean
    loading?: boolean
    /** Tra MỘT lô theo id với đúng bộ lọc của ô chọn (M-77: lô vừa quét tem).
     *  `undefined` = lô không chọn được ở ô này (hết tồn, sai loại...). Đi qua
     *  API danh sách vì vai trò ghi sổ không có quyền API chi tiết lô. Lô tra
     *  được được nhớ như lô đã thấy: nút hiện đúng nhãn, số dư dùng được ngay. */
    resolveById?: (id: string) => Promise<LotOption | undefined>
}

interface InfiniteLotQuery<T> {
    data?: { pages: Array<{ data?: T[] | null }> }
    hasNextPage: boolean
    isFetchingNextPage: boolean
    isLoading: boolean
    fetchNextPage: () => unknown
}

/** Id các lô seed CHƯA có số dư và chưa nằm trong trang đã tải — màn sửa
 *  phiếu seed từ item phiếu (không có số dư), lô ngoài trang đầu sẽ mất "Còn X"
 *  và nút "Lấy hết" nếu không tra riêng. Seed của sổ xưởng đã có số dư nên
 *  không tra (vai trò ghi sổ không có quyền API chi tiết lô). Nguồn đang tắt
 *  thì không tra: một form có thể đưa cùng seed cho hai nguồn khác loại lô
 *  (PNK lô thành phẩm: lô lấy hương / lô thành phẩm nguồn), tra nhầm loại là
 *  404. */
const NO_IDS: string[] = []
function useSeedIdsMissingStock<T extends { id: string }>(
    seed: LotOption[] | undefined,
    pages: Array<{ data?: T[] | null }> | undefined,
    enabled: boolean,
): string[] {
    return useMemo(() => {
        if (!enabled || !seed?.length) return NO_IDS
        const loaded = new Set(
            (pages ?? []).flatMap((p) => (p.data ?? []).map((l) => l.id)),
        )
        const ids = seed
            .filter((o) => o.available === undefined && !loaded.has(o.value))
            .map((o) => o.value)
        return ids.length ? ids : NO_IDS
    }, [seed, pages, enabled])
}

function useInfiniteLotSource<T>(
    query: InfiniteLotQuery<T>,
    setSearch: (search: string) => void,
    toOption: (item: T) => LotOption,
    seed: LotOption[] | undefined,
    seedStock: T[] = [],
    fetchById?: (id: string) => Promise<T | undefined>,
): LotPickerSource {
    // Mọi option từng thấy, để nhãn/số dư của lô đã chọn không mất khi từ khoá
    // đổi và trang hiện tại không còn chứa nó. Ghi trong useMemo: idempotent,
    // cùng pattern `seenRef` của ProductPicker.
    const seenRef = useRef<Map<string, LotOption>>(new Map())
    // Lô tra theo id (quét tem) nằm ngoài các trang đã tải: giữ trong state để
    // cũng có mặt trong danh sách chọn. Giữ BẢN GỐC từ API, đổi sang option mỗi
    // lần render như các trang đã tải — `toOption` đổi (vd phần mẻ khác đang
    // giữ vừa tải xong, M-140) thì số tồn của lô đã quét cũng đổi theo.
    const [resolved, setResolved] = useState<T[]>([])
    const pages = query.data?.pages
    const options = useMemo(() => {
        const map = new Map<string, LotOption>()
        // Số dư tra riêng cho seed: GIỮ nhãn/render của seed (vd lô đang chờ
        // duyệt hiện KL trong phiếu), chỉ gắn số dư + đơn vị.
        const stockById = new Map<string, LotOption>()
        for (const item of seedStock) {
            const opt = toOption(item)
            stockById.set(opt.value, opt)
        }
        // Seed trước, bản từ API sau: số dư/nhãn của API mới hơn.
        for (const opt of seed ?? []) {
            const stock =
                opt.available === undefined
                    ? stockById.get(opt.value)
                    : undefined
            map.set(
                opt.value,
                stock
                    ? {
                          ...opt,
                          available: stock.available,
                          unit: opt.unit ?? stock.unit,
                      }
                    : opt,
            )
        }
        for (const item of resolved) {
            const opt = toOption(item)
            map.set(opt.value, opt)
        }
        for (const page of pages ?? []) {
            for (const item of page.data ?? []) {
                const opt = toOption(item)
                map.set(opt.value, opt)
            }
        }
        for (const opt of map.values()) seenRef.current.set(opt.value, opt)
        return Array.from(map.values())
    }, [pages, seed, toOption, seedStock, resolved])

    const getOption = useCallback(
        (id: string | undefined) => (id ? seenRef.current.get(id) : undefined),
        [],
    )
    const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = query
    const onLoadMore = useCallback(() => {
        void fetchNextPage()
    }, [fetchNextPage])

    const resolveById = useMemo(() => {
        if (!fetchById) return undefined
        return async (id: string): Promise<LotOption | undefined> => {
            // Lô đã thấy (trang đã tải theo đúng bộ lọc, hoặc seed của bản ghi
            // đang sửa) chọn được sẵn — khỏi gọi mạng.
            const known = seenRef.current.get(id)
            if (known) return known
            const item = await fetchById(id)
            if (!item) return undefined
            const opt = toOption(item)
            seenRef.current.set(opt.value, opt)
            setResolved((prev) =>
                prev.some((i) => toOption(i).value === opt.value)
                    ? prev
                    : [...prev, item],
            )
            return opt
        }
    }, [fetchById, toOption])

    return {
        options,
        getOption,
        onSearchChange: setSearch,
        onLoadMore,
        hasMore: hasNextPage,
        loadingMore: isFetchingNextPage,
        loading: isLoading,
        resolveById,
    }
}

type Paging = 'page' | 'per_page' | 'search'

interface SourceOptions<TParams> {
    /** Bộ lọc riêng của chỗ dùng (chỉ lô còn tồn, nguồn đóng gói...). */
    filters?: Omit<TParams, Paging>
    /** Lô của bản ghi đang sửa: phải chọn/hiện được dù đã cạn tồn hay nằm ngoài
     *  trang đã tải. */
    seed?: LotOption[]
    /** Kiểu sổ xưởng: nút chỉ hiện tên + ngày, giấu mã lô (#M-16). */
    ledger?: boolean
    enabled?: boolean
    /** M-140: phần từng lô đang bị các mẻ CHƯA VÀO KHO khác của sổ giữ. Có thì
     *  số tồn của option (danh sách, gợi ý dưới ô, kiểm vượt tồn) đã trừ sẵn
     *  phần này — khớp số backend cho phép ghi. Không truyền = tồn thô. */
    held?: ReadonlyMap<string, number>
}

/** Trừ phần đang bị giữ khỏi tồn của một lô từ API (không âm: mẻ nháp cũ từ
 *  trước khi có kiểm tồn có thể giữ nhiều hơn tồn). */
export function lessHeld<T extends { id: string; quantity_available: number }>(
    item: T,
    held: ReadonlyMap<string, number> | undefined,
): T {
    const h = held?.get(item.id)
    if (!h) return item
    return {
        ...item,
        quantity_available: Math.max(0, item.quantity_available - h),
    }
}

/** Như `lessHeld` cho seed (lô của mẻ đang sửa) — seed không đi qua `toOption`. */
function seedLessHeld(
    seed: LotOption[] | undefined,
    held: ReadonlyMap<string, number> | undefined,
): LotOption[] | undefined {
    if (!seed || !held?.size) return seed
    return seed.map((o) => {
        const h = held.get(o.value)
        return h && o.available !== undefined
            ? { ...o, available: Math.max(0, o.available - h) }
            : o
    })
}

/** Từ khoá gõ trong ô tìm + bản đã debounce gửi lên API. */
function useDebouncedSearch() {
    const [search, setSearch] = useState('')
    const debounced = useDebounced(search, 300)
    return { search: debounced || undefined, setSearch }
}

export function useSortingLotSource({
    filters,
    seed,
    ledger = false,
    enabled = true,
    held,
}: SourceOptions<SortingListParams> = {}): LotPickerSource {
    const { search, setSearch } = useDebouncedSearch()
    const query = useSortingLotsInfinite(
        { ...filters, per_page: LOT_PICKER_PAGE_SIZE, search },
        { enabled },
    )
    const seedStock = useSortingLotsByIds(
        useSeedIdsMissingStock(seed, query.data?.pages, enabled),
    )
    const seedOptions = useMemo(() => seedLessHeld(seed, held), [seed, held])
    const toOption = useCallback(
        (l: Parameters<typeof sortingLotOption>[0]) =>
            sortingLotOption(lessHeld(l, held), ledger),
        [ledger, held],
    )
    const fetchById = useCallback(
        async (id: string) =>
            (await sortingLotApi.listLots({ ...filters, id, per_page: 1 }))
                .data?.[0],
        [filters],
    )
    return useInfiniteLotSource(
        query,
        setSearch,
        toOption,
        seedOptions,
        seedStock,
        fetchById,
    )
}

export function useScentingLotSource({
    filters,
    seed,
    ledger = false,
    enabled = true,
    held,
}: SourceOptions<ScentingListParams> = {}): LotPickerSource {
    const { search, setSearch } = useDebouncedSearch()
    const query = useScentingLotsInfinite(
        { ...filters, per_page: LOT_PICKER_PAGE_SIZE, search },
        { enabled },
    )
    const seedStock = useScentingLotsByIds(
        useSeedIdsMissingStock(seed, query.data?.pages, enabled),
    )
    const seedOptions = useMemo(() => seedLessHeld(seed, held), [seed, held])
    const toOption = useCallback(
        (l: Parameters<typeof scentingLotOption>[0]) =>
            scentingLotOption(lessHeld(l, held), ledger),
        [ledger, held],
    )
    const fetchById = useCallback(
        async (id: string) =>
            (await scentingLotApi.listLots({ ...filters, id, per_page: 1 }))
                .data?.[0],
        [filters],
    )
    return useInfiniteLotSource(
        query,
        setSearch,
        toOption,
        seedOptions,
        seedStock,
        fetchById,
    )
}

export function useProductLotSource({
    filters,
    seed,
    ledger = false,
    enabled = true,
    held,
}: SourceOptions<ProductListParams> = {}): LotPickerSource {
    const { search, setSearch } = useDebouncedSearch()
    const query = useProductLotsInfiniteQuery(
        { ...filters, per_page: LOT_PICKER_PAGE_SIZE, search },
        { enabled },
    )
    const seedStock = useProductLotsByIds(
        useSeedIdsMissingStock(seed, query.data?.pages, enabled),
    )
    const seedOptions = useMemo(() => seedLessHeld(seed, held), [seed, held])
    const toOption = useCallback(
        (l: Parameters<typeof productLotToOption>[0]) =>
            productLotToOption(lessHeld(l, held), ledger),
        [ledger, held],
    )
    const fetchById = useCallback(
        async (id: string) =>
            (await productLotApi.listLots({ ...filters, id, per_page: 1 }))
                .data?.[0],
        [filters],
    )
    return useInfiniteLotSource(
        query,
        setSearch,
        toOption,
        seedOptions,
        seedStock,
        fetchById,
    )
}

export function usePackagingLotSource({
    filters,
    seed,
    enabled = true,
}: Omit<SourceOptions<PackagingListParams>, 'ledger'> = {}): LotPickerSource {
    const { search, setSearch } = useDebouncedSearch()
    const query = usePackagingLotsInfinite(
        { ...filters, per_page: LOT_PICKER_PAGE_SIZE, search },
        { enabled },
    )
    const seedStock = usePackagingLotsByIds(
        useSeedIdsMissingStock(seed, query.data?.pages, enabled),
    )
    return useInfiniteLotSource(
        query,
        setSearch,
        packagingLotOption,
        seed,
        seedStock,
    )
}
